import React, { useEffect, useMemo, useRef, useState } from "react";

import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import routes from "pages/notice/static/route";
import { getUserInfo } from "shared/api/AuthApi";
import { toFileKey, uploadToS3 } from "shared/api/awsS3";
import { useDirtyCheck } from "shared/lib/form/useDirtyCheck";
import CircularProgressIndicator from "shared/ui/CircularProgressIndicator";
import { alertDialog, confirmDialog, confirmLeaveIfDirty } from "shared/ui/feedback";
import { ChipGroup } from "shared/ui/filters";
import { FormPageShell } from "shared/ui/layout";
import { isEmptyImageSrc } from "shared/ui/media";
import {
  createUploadItem,
  FileDropzone,
  ImageUploadBox,
  isExistingAttachment,
  toUploadItem,
  UPLOAD_STATUS,
  uploadItems,
} from "shared/ui/upload";

import "bootstrap/dist/css/bootstrap.min.css";

const NOTICE_FORCE_PLAIN_TEXT =
  process.env.REACT_APP_NOTICE_FORCE_PLAIN_TEXT === "true";

const UPLOAD_STATE_MSG = {
  0: "이미지 업로드 중..",
  1: "첨부파일 업로드 중..",
  2: "내용 저장 중..",
};

const COPY = {
  create: {
    header: "공지사항 작성",
    submit: "등록하기",
    confirm: "공지사항을 등록하시겠습니까?\n해당 내용으로 공지가 등록됩니다.",
    leave: "공지 등록을 취소하시겠습니까?\n취소시 작성 중인 내용이 사라집니다.",
    attachVerb: "등록",
    failTitle: "공지 글 추가 실패",
    failVerb: "추가",
  },
  edit: {
    header: "공지사항 수정",
    submit: "수정하기",
    confirm: "공지사항을 수정하시겠습니까?\n해당 내용으로 공지가 수정됩니다.",
    leave: "공지 편집을 취소하시겠습니까?\n취소시 작성 중인 내용이 사라집니다.",
    attachVerb: "수정",
    failTitle: "공지 글 편집 실패",
    failVerb: "편집",
  },
};

const KO_TOOLBAR = {
  placeholder: "내용을 입력하세요.",
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "link",
      "bulletedList",
      "numberedList",
      "blockQuote",
    ],
  },
};
const EN_TOOLBAR = { ...KO_TOOLBAR, placeholder: "Write a content." };

// 공지 대상(작성 언어) 옵션.
//  - all : 한/영 모두 직접 작성 (기존 동작)
//  - ko  : 한국어 전용 → 영어 입력칸은 비활성 + 안내 문구 자동 기입
//  - en  : 영어 전용 → 한국어 입력칸은 비활성 + 안내 문구 자동 기입
const NOTICE_TARGETS = [
  { value: "all", label: "전체" },
  { value: "ko", label: "한국어" },
  { value: "en", label: "영어" },
];

// 비활성 쪽에 자동으로 채워 넣는 안내 문구.
//  - en.* : 한국어 전용 공지의 "영어" 칸에 들어갈 영문 안내 (영어 독자에게 한국어로 보라고 안내)
//  - ko.* : 영어 전용 공지의 "한국어" 칸에 들어갈 국문 안내 (한국어 독자에게 영어로 보라고 안내)
// 본문은 CKEditor(HTML)이므로 <p>로 감싼다. 수정 모드 진입 시 제목으로 대상을 역판별한다.
const TARGET_NOTICE_TEXT = {
  en: {
    title: "This notice is written in Korean.",
    content:
      "<p>This notice is available in Korean only. Please switch to Korean using the language toggle at the top.</p>",
  },
  ko: {
    title: "이 공지는 영어로 작성되었습니다.",
    content:
      "<p>이 공지는 영어로만 제공됩니다. 상단의 언어 전환 버튼(EN/한국어)을 눌러 확인해 주세요.</p>",
  },
};

// 대상값 → 각 언어 칸의 비활성 여부.
const isKoDisabled = (target) => target === "en";
const isEnDisabled = (target) => target === "ko";

// 기존 공지 데이터로부터 대상을 역판별(수정 모드). 제목이 안내 문구와 정확히 일치하면 전용 공지로 본다.
const detectTarget = (title, engTitle) => {
  if (engTitle === TARGET_NOTICE_TEXT.en.title) return "ko";
  if (title === TARGET_NOTICE_TEXT.ko.title) return "en";
  return "all";
};

// 첨부파일/썸네일을 비교 가능한 문자열로 (추가/삭제/교체 시 값이 달라진다)
const fileSignature = (list) =>
  (Array.isArray(list) ? list : [])
    .map(
      (file) =>
        file?.id ??
        file?.name ??
        file?.url ??
        file?.downloadLink ??
        (typeof file === "string" ? file : ""),
    )
    .join("|");

const thumbnailSignature = (thumb) => {
  if (!thumb) return "";
  if (typeof thumb === "string") return thumb;
  return thumb.name ?? thumb.imageUrl ?? thumb.url ?? "new";
};

const getReadableError = (error) => {
  const serverData = error?.response?.data;
  if (typeof serverData === "string" && serverData.trim() !== "") {
    return serverData;
  }
  if (serverData && typeof serverData === "object") {
    if (serverData.message) return serverData.message;
    return JSON.stringify(serverData);
  }
  return error?.message || String(error);
};

const parsePositiveInteger = (value) => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0 ? num : null;
};

const htmlToPlainText = (html) => {
  if (typeof html !== "string" || html.trim() === "") return "";
  if (typeof window !== "undefined" && window.DOMParser) {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return (doc.body?.textContent || "").trim();
  }
  return html.replace(/<[^>]*>/g, "").trim();
};

/**
 * 공지사항 등록·수정 공용 폼.
 *
 * 내부적으로는 수정 모델(기존 첨부/신규 첨부/삭제 목록, 기존 썸네일/신규 썸네일)을
 * 그대로 사용한다. 등록은 기존 값이 빈 값일 뿐이라 자연히 포섭된다.
 * 데이터 로드/뮤테이션은 래퍼가 담당한다.
 *
 * @param {"create"|"edit"} mode
 * @param {object|null} initialValues - {title, pin, files, image, content, engTitle, engContent}
 * @param {(data:object)=>Promise<void>} onSubmit - 생성/수정 API 호출
 */
export function NoticeForm({ mode, initialValues = null, onSubmit }) {
  const copy = COPY[mode];
  const navigate = useNavigate();

  const [loadingState, setLoadingState] = useState(false);
  const [uploadProcessState, setUploadProcessState] = useState(null);

  const [noticeTitle, setNoticeTitle] = useState("");
  const [noticePin, setNoticePin] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loadedFiles, setLoadedFiles] = useState([]);
  const [delFiles, setDelFiles] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [loadedThumbnail, setLoadedThumbnail] = useState(null);
  const thumbnailUploadRef = useRef({ file: null, key: null });
  const [content, setContent] = useState("");
  const [engTitle, setEngTitle] = useState("");
  const [engContent, setEngContent] = useState("");
  const [target, setTarget] = useState("all");
  // 대상 전환으로 비활성화되기 직전의 실제 입력값을 보관했다가, 다시 활성화되면 복원한다.
  const stashRef = useRef({ ko: null, en: null });

  const { isDirty, captureBaseline } = useDirtyCheck({
    title: noticeTitle,
    pin: noticePin,
    target,
    content,
    engTitle,
    engContent,
    loadedFiles: fileSignature(loadedFiles),
    newFiles: fileSignature(selectedFiles),
    thumbnail: thumbnailSignature(loadedThumbnail),
    newThumbnail: thumbnailSignature(selectedThumbnail),
  });

  // 초기값 1회 주입 + 변경 비교 기준값 기록 (create: 빈 값 / edit: 로드된 값).
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || !initialValues) return;
    initializedRef.current = true;
    // 레거시 데이터 방어: 기존 공지에 박힌 default-notice URL/빈 값은 "썸네일 없음"으로 취급.
    // 그래야 수정 진입 시 실체 없는 이미지가 선택된 것처럼 보이지 않는다.
    const initialImage = isEmptyImageSrc(initialValues.image)
      ? null
      : initialValues.image;
    const detectedTarget = detectTarget(
      initialValues.title || "",
      initialValues.engTitle || "",
    );
    setNoticeTitle(initialValues.title || "");
    setNoticePin(Boolean(initialValues.pin));
    setLoadedFiles(initialValues.files || []);
    setLoadedThumbnail(initialImage);
    setContent(initialValues.content || "");
    setEngTitle(initialValues.engTitle || "");
    setEngContent(initialValues.engContent || "");
    setTarget(detectedTarget);
    captureBaseline({
      title: initialValues.title || "",
      pin: Boolean(initialValues.pin),
      target: detectedTarget,
      content: initialValues.content || "",
      engTitle: initialValues.engTitle || "",
      engContent: initialValues.engContent || "",
      loadedFiles: fileSignature(initialValues.files),
      newFiles: "",
      thumbnail: thumbnailSignature(initialImage),
      newThumbnail: "",
    });
  }, [initialValues, captureBaseline]);

  // 로그인 사용자 id를 보관(작성자 식별). 양쪽 모드 모두 무해.
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        const candidates = [
          userInfo?.userId,
          userInfo?.id,
          userInfo?.appUserId,
          userInfo?.admin?.userId,
          userInfo?.admin?.id,
        ];
        for (const candidate of candidates) {
          const parsed = parsePositiveInteger(candidate);
          if (parsed !== null) {
            localStorage.setItem("userId", String(parsed));
            break;
          }
        }
      } catch (error) {
        // 사용자 정보 조회 실패는 치명적이지 않으므로 무시.
      }
    };
    fetchUserInfo();
  }, []);

  // 대상별 필수 입력 판정. 전용 공지는 해당 언어 칸만 채우면 되며,
  // 비활성 쪽은 안내 문구가 자동 기입되어 payload가 항상 한/영 모두 채워진 채 전송된다.
  const koFilled = Boolean(noticeTitle) && content !== "";
  const enFilled = Boolean(engTitle) && engContent !== "";
  const isAllFieldsFilled =
    target === "ko" ? koFilled : target === "en" ? enFilled : koFilled && enFilled;
  const canSubmit = isAllFieldsFilled && isDirty && !loadingState;

  // 드롭존에 보여줄 목록 = 기존 첨부(loadedFiles) + 새로 고른 파일(selectedFiles).
  const attachmentList = [
    ...(Array.isArray(loadedFiles) ? loadedFiles : []),
    ...(Array.isArray(selectedFiles) ? selectedFiles : []),
  ];

  const handleSetPin = () => setNoticePin((prev) => !prev);

  // 대상 전환: 새로 비활성화되는 쪽은 현재 입력값을 stash 후 안내 문구로 채우고,
  // 다시 활성화되는 쪽은 stash해 둔 값으로 복원한다. (ko↔en 직접 전환도 양쪽 모두 처리)
  const handleTargetChange = (next) => {
    if (next === target) return;
    const enWasDisabled = isEnDisabled(target);
    const enWillDisabled = isEnDisabled(next);
    const koWasDisabled = isKoDisabled(target);
    const koWillDisabled = isKoDisabled(next);

    if (!enWasDisabled && enWillDisabled) {
      stashRef.current.en = { engTitle, engContent };
      setEngTitle(TARGET_NOTICE_TEXT.en.title);
      setEngContent(TARGET_NOTICE_TEXT.en.content);
    } else if (enWasDisabled && !enWillDisabled) {
      setEngTitle(stashRef.current.en?.engTitle ?? "");
      setEngContent(stashRef.current.en?.engContent ?? "");
      stashRef.current.en = null;
    }

    if (!koWasDisabled && koWillDisabled) {
      stashRef.current.ko = { noticeTitle, content };
      setNoticeTitle(TARGET_NOTICE_TEXT.ko.title);
      setContent(TARGET_NOTICE_TEXT.ko.content);
    } else if (koWasDisabled && !koWillDisabled) {
      setNoticeTitle(stashRef.current.ko?.noticeTitle ?? "");
      setContent(stashRef.current.ko?.content ?? "");
      stashRef.current.ko = null;
    }

    setTarget(next);
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files) {
      setSelectedFiles((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...Array.from(files).map(createUploadItem),
      ]);
    }
  };

  // 추가/삭제 시 기존/신규로 다시 가르고, 삭제된 기존 첨부(id)는 delFiles에 모은다.
  const handleAttachmentsChange = (next) => {
    const list = (Array.isArray(next) ? next : []).map(toUploadItem);
    const removedExistingIds = (Array.isArray(loadedFiles) ? loadedFiles : [])
      .filter(
        (file) =>
          isExistingAttachment(file) && file.id != null && !list.includes(file),
      )
      .map((file) => file.id);
    if (removedExistingIds.length > 0) {
      setDelFiles((prev) => [...prev, ...removedExistingIds]);
    }
    setLoadedFiles(list.filter(isExistingAttachment));
    setSelectedFiles(list.filter((file) => !isExistingAttachment(file)));
  };

  // 썸네일 삭제(null) 시 기존 썸네일도 비워 화면 제거 + 저장 시 빈 값 전송.
  const handleSetThumbnail = (next) => {
    setSelectedThumbnail(next ?? null);
    if (next == null) setLoadedThumbnail(null);
  };

  const getExistingThumbnailUrl = () => {
    if (typeof loadedThumbnail === "string") return loadedThumbnail;
    if (loadedThumbnail?.imageUrl) return loadedThumbnail.imageUrl;
    if (loadedThumbnail?.url) return loadedThumbnail.url;
    return "";
  };

  // 미리보기 URL: 새로 고른 파일은 objectURL, 아니면 기존 썸네일 URL.
  const thumbnailPreview = useMemo(() => {
    if (selectedThumbnail instanceof Blob) {
      return URL.createObjectURL(selectedThumbnail);
    }
    if (typeof loadedThumbnail === "string") return loadedThumbnail;
    if (loadedThumbnail?.imageUrl) return loadedThumbnail.imageUrl;
    if (loadedThumbnail?.url) return loadedThumbnail.url;
    return "";
  }, [selectedThumbnail, loadedThumbnail]);

  const handleLeave = async () => {
    const shouldCancel = await confirmLeaveIfDirty(isDirty, copy.leave);
    if (shouldCancel) navigate(routes.notice);
  };

  const handleSubmit = async () => {
    if (loadingState) return;
    if (noticeTitle === "" || engTitle === "") {
      alertDialog("제목을 입력해주세요");
      return;
    }
    if (noticeTitle.length > 255 || engTitle.length > 255) {
      alertDialog("제목은 255자 제한입니다");
      return;
    }
    if (content === "" || engContent === "") {
      alertDialog("내용을 입력해주세요");
      return;
    }
    if (content.length > 65000 || engContent.length > 65000) {
      alertDialog("본문은 65000자 제한입니다 ");
      return;
    }

    const shouldProceed = await confirmDialog(copy.confirm);
    if (!shouldProceed) return;

    setLoadingState(true);
    try {
      // 1) 썸네일 업로드 (성공분 캐시로 재시도 시 중복 업로드 방지). 실패 시 중단.
      //    서버가 imageKey 필드에 S3 key를 기대하므로 URL이 아닌 key를 보관/전송한다.
      let imageKey;
      if (selectedThumbnail) {
        setUploadProcessState(0);
        try {
          if (
            thumbnailUploadRef.current.file === selectedThumbnail &&
            thumbnailUploadRef.current.key
          ) {
            imageKey = thumbnailUploadRef.current.key;
          } else {
            const uploadedImage = await uploadToS3(selectedThumbnail, true);
            imageKey = uploadedImage.key;
            thumbnailUploadRef.current = {
              file: selectedThumbnail,
              key: imageKey,
            };
          }
        } catch (error) {
          alertDialog(
            "썸네일 이미지 업로드에 실패했습니다. 다시 시도해주세요.\n실패 사유: " +
              getReadableError(error),
            { title: "썸네일 업로드 실패" },
          );
          return;
        } finally {
          setUploadProcessState(null);
        }
      }

      // 2) 첨부파일 업로드 (부분 성공 허용)
      setUploadProcessState(1);
      const uploaded = await uploadItems(selectedFiles);
      setSelectedFiles(uploaded);
      setUploadProcessState(null);

      const failedCount = uploaded.filter(
        (item) => item.status === UPLOAD_STATUS.FAILED,
      ).length;
      if (failedCount > 0) {
        alertDialog(
          `${failedCount}개 첨부파일 업로드에 실패했습니다.\n표에서 실패한 파일을 확인 후 다시 시도하거나 삭제한 뒤 ${copy.attachVerb}해주세요.`,
          { title: "첨부파일 업로드 실패" },
        );
        return;
      }

      // 3) DB 저장 (백엔드는 urls/titles를 기존 목록에 추가 → 새 첨부만 보낸다)
      setUploadProcessState(2);
      const remainingExistingCount = (
        Array.isArray(loadedFiles) ? loadedFiles : []
      ).filter(isExistingAttachment).length;
      const okItems = uploaded.filter(
        (item) => item.status === UPLOAD_STATUS.UPLOADED && item.url,
      );
      const normalize = (html) =>
        NOTICE_FORCE_PLAIN_TEXT ? htmlToPlainText(html) : html;

      const data = {
        titles: okItems.map(
          (_, index) =>
            `${noticeTitle} 첨부파일 (${remainingExistingCount + index + 1})`,
        ),
        urls: okItems.map((item) => item.key),
        title: noticeTitle,
        engTitle,
        // 썸네일 미선택 시 빈 값 전송 — 실체 없는 기본 이미지 URL을 박지 않는다.
        // 표시단에서 EmptyImage 자리표시로 처리한다.
        // 재업로드 안 한 기존 썸네일은 전체 URL이라 key로 변환해 보낸다.
        image: imageKey || toFileKey(getExistingThumbnailUrl()) || "",
        content: normalize(content),
        engContent: normalize(engContent),
        pin: noticePin,
        ...(mode === "edit" ? { delFiles } : {}),
      };
      await onSubmit(data);
      navigate(routes.notice);
    } catch (error) {
      alertDialog(
        `해당 공지를 ${copy.failVerb} 중 에러가 발생했습니다. \n같은 현상이 반복되면 관리자에게 문의해주시기 바랍니다. \n실패 사유: ` +
          getReadableError(error),
        { title: copy.failTitle },
      );
    } finally {
      setLoadingState(false);
      setUploadProcessState(null);
    }
  };

  return (
    <FormPageShell
      title={copy.header}
      onBack={handleLeave}
      submitLabel={copy.submit}
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
    >
      <CircularProgressIndicator
        loading={loadingState}
        msg={uploadProcessState != null ? UPLOAD_STATE_MSG[uploadProcessState] : null}
      />

      {/* 공지 옵션 섹션 */}
      <div className="mb-3 p-3 bg-white">
        <div className="col-sm-12 d-flex">
          {/* left section */}
          <div className="col-sm-8">
            <div className="row">
              <div className="col-sm-12 d-flex align-items-center">
                <label className="col-sm-2 col-form-label">
                  제목<RedAsterisk>*</RedAsterisk>
                </label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    className="form-control me-2"
                    placeholder="제목 입력"
                    value={noticeTitle}
                    style={{ maxWidth: "600px" }}
                    onChange={(e) => setNoticeTitle(e.target.value)}
                    disabled={isKoDisabled(target)}
                  />
                </div>
              </div>
            </div>
            <MarginDiv />
            <div className="row">
              <div className="col-sm-12 d-flex align-items-start">
                <label className="col-sm-2 col-form-label">첨부파일</label>
                <div className="col-sm-6">
                  <input
                    type="file"
                    className="form-control visually-hidden"
                    id="fileInput"
                    accept=".jpg, .jpeg, .png, .pdf"
                    onChange={handleFileSelect}
                  />
                  <label
                    htmlFor="fileInput"
                    className="form-label btn btn-light border"
                  >
                    내 PC
                  </label>
                  <FileDropzone
                    files={attachmentList}
                    onChange={handleAttachmentsChange}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-12 d-flex align-items-center">
                <label className="col-sm-2 col-form-label">고정 여부</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={noticePin}
                    onChange={handleSetPin}
                  />{" "}
                  고정
                </div>
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-sm-12 d-flex align-items-center">
                <label className="col-sm-2 col-form-label">대상</label>
                <ChipGroup
                  options={NOTICE_TARGETS}
                  value={target}
                  onChange={handleTargetChange}
                  allowDeselect={false}
                />
              </div>
            </div>
          </div>

          {/* right section */}
          <div className="col-sm-4">
            <div className="row h-100">
              <div className="col-sm-12 d-flex justify-content-center">
                <ImageUploadBox
                  imageUrl={thumbnailPreview}
                  onSelectFile={(file) => handleSetThumbnail(file)}
                  onRemove={() => handleSetThumbnail(null)}
                  title="썸네일 이미지 삽입"
                  width="238px"
                  height="238px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <TextEditor>
        <CKEditor
          editor={ClassicEditor}
          data={content}
          onChange={(event, editor) => setContent(editor.getData())}
          config={KO_TOOLBAR}
          language="ko"
          disabled={isKoDisabled(target)}
        />
      </TextEditor>
      <RowDiv style={{ margin: "20px 0" }}>
        <Header3>English</Header3>
        <Title>
          상단의 고정 여부, 썸네일은 영문 페이지에서 동일하게 적용됩니다
        </Title>
      </RowDiv>
      <RegisterContentsDiv>
        <RegisterDiv>
          <RowDiv>
            <Title>
              Title<RedAsterisk>*</RedAsterisk>
            </Title>
            <TextInput
              type="text"
              placeholder="Write a title"
              value={engTitle}
              onChange={(e) => setEngTitle(e.target.value)}
              disabled={isEnDisabled(target)}
            />
          </RowDiv>
        </RegisterDiv>
      </RegisterContentsDiv>
      <TextEditor>
        <CKEditor
          editor={ClassicEditor}
          data={engContent}
          onChange={(_, editor) => setEngContent(editor.getData())}
          config={EN_TOOLBAR}
          language="en"
          disabled={isEnDisabled(target)}
        />
      </TextEditor>
    </FormPageShell>
  );
}

export default NoticeForm;

const TextEditor = styled.div`
  width: 100%;
  height: 350px;
  margin-top: 20px;
  overflow: auto;

  .ck-editor__editable {
    height: 310px !important;
    overflow-y: scroll;
    scrollbar-width: thin;
    scrollbar-color: #ccc transparent;
  }
`;

const MarginDiv = styled.div`
  margin-top: 16px;
`;

const RedAsterisk = styled.span`
  font-size: ${(props) => props.theme.Web_fontSizes.Body3};
  font-weight: ${(props) => props.theme.fontWeights.Body3};
  line-height: ${(props) => props.theme.LineHeight.Body3};
  letter-spacing: -0.56px;
  font-style: normal;
  font-family: "Noto Sans KR";
  text-align: center;
  color: red;
`;

const Header3 = styled.header`
  color: #000;
  font-family: "NotoSansKRBold";
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`;

const RowDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 40px;
  justify-content: flex-start;
  align-items: center;
`;

const Title = styled.div`
  white-space: nowrap;
  color: var(--text-black, #111);
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const RegisterDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px;
  gap: 15px;
`;

const RegisterContentsDiv = styled.div`
  display: flex;
  width: 100%;
  background-color: white;
`;

const TextInput = styled.input`
  width: 100%;
  height: 35px;
  border-radius: 2px;
  border: 1px solid #e8e8e8;
  padding: 2px 10px;
  &::placeholder {
    opacity: 0.2;
  }
  &:disabled {
    background-color: #f2f2f2;
    color: #8a8a8a;
    cursor: not-allowed;
  }
`;
